import { useEffect, useState, useCallback } from "react";
import { TopBar } from "../components/layout/TopBar";
import { Spinner } from "../components/shared/Spinner";
import { Button } from "../components/shared/Button";
import { EmptyState } from "../components/shared/EmptyState";
import * as ui from "../styles/ui";
import { api, type Snippet } from "../api/client";
import { useToastStore } from "../stores/useToastStore";
import { slugify } from "../../shared/doc-paths";

const ALL_CATEGORIES = "all";

export function SnippetsPage() {
  const [snippets, setSnippets] = useState<Snippet[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState(ALL_CATEGORIES);

  // ── Create/edit modal state ──
  const [showModal, setShowModal] = useState(false);
  const [editingSlug, setEditingSlug] = useState<string | null>(null);
  const [formTitle, setFormTitle] = useState("");
  const [formCategory, setFormCategory] = useState("");
  const [formDescription, setFormDescription] = useState("");
  const [formContent, setFormContent] = useState("");
  const [saving, setSaving] = useState(false);
  const [deletingSlug, setDeletingSlug] = useState<string | null>(null);

  const addToast = useToastStore((s) => s.addToast);

  // Synchronous setState stays out of this function so the mount effect
  // doesn't trip react-hooks/set-state-in-effect; callers that re-fetch
  // (save) flip `loading` themselves from their event handler.
  const loadSnippets = useCallback(() => {
    api.snippets
      .list()
      .then((data) => {
        setSnippets(data);
        setError(null);
      })
      .catch((e) => {
        const detail =
          e instanceof Error ? e.message : "Failed to load snippets";
        setError(
          detail.includes("503")
            ? "GitHub is not configured. Add GITHUB_TOKEN and GITHUB_REPO to the worker."
            : detail,
        );
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadSnippets();
  }, [loadSnippets]);

  const categories = [
    ...new Set(snippets.map((s) => s.category || "uncategorized")),
  ].sort();
  const visible =
    categoryFilter === ALL_CATEGORIES
      ? snippets
      : snippets.filter(
          (s) => (s.category || "uncategorized") === categoryFilter,
        );

  const openCreateModal = useCallback(() => {
    setEditingSlug(null);
    setFormTitle("");
    setFormCategory("");
    setFormDescription("");
    setFormContent("");
    setShowModal(true);
  }, []);

  const openEditModal = useCallback((snippet: Snippet) => {
    setEditingSlug(snippet.slug);
    setFormTitle(snippet.title);
    setFormCategory(snippet.category);
    setFormDescription(snippet.description);
    setFormContent(snippet.content);
    setShowModal(true);
  }, []);

  const handleSave = useCallback(async () => {
    const slug = editingSlug ?? slugify(formTitle);
    if (!slug || !formTitle.trim()) {
      addToast("set a title first", "error");
      return;
    }
    setSaving(true);
    try {
      await api.snippets.save(slug, {
        title: formTitle.trim(),
        category: formCategory.trim(),
        description: formDescription.trim(),
        content: formContent,
      });
      addToast("snippet saved", "success");
      setShowModal(false);
      setLoading(true);
      loadSnippets();
    } catch {
      addToast("save failed", "error");
    } finally {
      setSaving(false);
    }
  }, [
    editingSlug,
    formTitle,
    formCategory,
    formDescription,
    formContent,
    addToast,
    loadSnippets,
  ]);

  const handleDelete = useCallback(
    async (snippet: Snippet) => {
      if (
        !window.confirm(
          `Delete snippet "${snippet.title}"? This removes snippets/${snippet.slug}.md from the docs repo (git history keeps the old version).`,
        )
      ) {
        return;
      }
      setDeletingSlug(snippet.slug);
      try {
        await api.snippets.remove(snippet.slug);
        addToast("snippet deleted", "success");
        setSnippets((prev) => prev.filter((s) => s.slug !== snippet.slug));
      } catch {
        addToast("delete failed", "error");
      } finally {
        setDeletingSlug(null);
      }
    },
    [addToast],
  );

  return (
    <>
      <TopBar
        title="snippets"
        actions={
          <Button variant="primary" onClick={openCreateModal}>
            new snippet
          </Button>
        }
      />
      <div style={ui.pageContent}>
        {loading ? (
          <div style={styles.loadingWrap}>
            <Spinner size={24} label="loading…" />
          </div>
        ) : error ? (
          <EmptyState title="could not load snippets" body={error} />
        ) : snippets.length === 0 ? (
          <EmptyState
            title="no snippets yet"
            body="reusable blocks, insertable from the editor"
            action={{ label: "create snippet", onClick: openCreateModal }}
          />
        ) : (
          <>
            <div style={styles.filterRow}>
              {[ALL_CATEGORIES, ...categories].map((cat) => (
                <button
                  key={cat}
                  onClick={() => setCategoryFilter(cat)}
                  style={{
                    ...styles.filterChip,
                    ...(categoryFilter === cat ? styles.filterChipActive : {}),
                  }}
                >
                  {cat === ALL_CATEGORIES ? `all (${snippets.length})` : cat}
                </button>
              ))}
            </div>

            <div style={styles.grid}>
              {visible.map((snippet) => (
                <div
                  key={snippet.slug}
                  style={styles.card}
                  role="button"
                  tabIndex={0}
                  onClick={() => openEditModal(snippet)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      openEditModal(snippet);
                    }
                  }}
                >
                  <h3 style={styles.cardTitle}>{snippet.title}</h3>
                  <pre style={styles.cardPreview}>
                    {snippet.content.slice(0, 160)}
                    {snippet.content.length > 160 ? "…" : ""}
                  </pre>
                  <div style={styles.cardFooter}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(snippet);
                      }}
                      disabled={deletingSlug === snippet.slug}
                      style={styles.deleteBtn}
                    >
                      {deletingSlug === snippet.slug ? "deleting..." : "delete"}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* ── Create/Edit Modal ── */}
      {showModal && (
        <div style={modalStyles.overlay}>
          <div style={modalStyles.card}>
            <h3 style={modalStyles.heading}>
              {editingSlug ? `edit snippet — ${editingSlug}` : "new snippet"}
            </h3>

            <label style={styles.fieldLabel}>Title</label>
            <input
              type="text"
              value={formTitle}
              onChange={(e) => setFormTitle(e.target.value)}
              placeholder="Standard Disclaimer"
              style={styles.fieldInput}
              autoFocus
            />
            {!editingSlug && formTitle.trim() && (
              <p style={modalStyles.slugHint}>
                snippets/{slugify(formTitle)}.md
              </p>
            )}

            <label style={styles.fieldLabel}>Category</label>
            <input
              type="text"
              value={formCategory}
              onChange={(e) => setFormCategory(e.target.value)}
              placeholder="disclaimer, governance, formula..."
              style={styles.fieldInput}
            />

            <label style={styles.fieldLabel}>Description</label>
            <input
              type="text"
              value={formDescription}
              onChange={(e) => setFormDescription(e.target.value)}
              placeholder="When to use this snippet"
              style={styles.fieldInput}
            />

            <label style={styles.fieldLabel}>Markdown</label>
            <textarea
              value={formContent}
              onChange={(e) => setFormContent(e.target.value)}
              placeholder="Snippet markdown..."
              style={modalStyles.contentArea}
              spellCheck={false}
            />

            <div style={modalStyles.btnRow}>
              <Button onClick={() => setShowModal(false)} disabled={saving}>
                cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleSave}
                disabled={saving || !formTitle.trim()}
              >
                {saving
                  ? "saving..."
                  : editingSlug
                    ? "save changes"
                    : "create snippet"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

const styles: Record<string, React.CSSProperties> = {
  loadingWrap: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "40vh",
  },
  filterRow: {
    display: "flex",
    flexWrap: "wrap" as const,
    gap: 10,
    marginBottom: "var(--space-4)",
  },
  filterChip: {
    fontFamily: "var(--font-mono)",
    padding: "2px 0",
    border: "none",
    borderBottom: "2px solid transparent",
    borderRadius: 0,
    fontSize: 11,
    fontWeight: 500,
    color: "var(--fg-3)",
    background: "transparent",
    cursor: "pointer",
    transition: "border-color 0.15s, color 0.15s",
  },
  filterChipActive: {
    color: "var(--fg-1)",
    borderBottom: "2px solid var(--fg-1)",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: "var(--space-4)",
    maxWidth: 880,
  },
  card: {
    background: "var(--comp-paper)",
    border: "1px solid var(--comp-rule)",
    borderRadius: 0,
    padding: "var(--space-3)",
    display: "flex",
    flexDirection: "column",
    gap: 6,
    cursor: "pointer",
    transition: "border-color 0.15s",
  },
  cardTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: "var(--fg-1)",
  },
  cardPreview: {
    margin: 0,
    padding: "8px 0 0",
    borderTop: "1px solid var(--comp-rule)",
    fontSize: 11,
    lineHeight: 1.5,
    fontFamily: "var(--font-mono)",
    color: "var(--fg-2)",
    whiteSpace: "pre-wrap" as const,
    wordBreak: "break-word" as const,
    maxHeight: 76,
    overflow: "hidden",
  },
  cardFooter: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    marginTop: 4,
  },
  deleteBtn: {
    padding: 0,
    border: "none",
    fontSize: 11,
    fontWeight: 500,
    fontFamily: "var(--font-mono)",
    color: "var(--comp-salmon)",
    background: "none",
    cursor: "pointer",
    textDecoration: "underline",
    textUnderlineOffset: 2,
  },
  fieldLabel: {
    display: "block",
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: "0.08em",
    textTransform: "lowercase" as const,
    color: "var(--fg-3)",
    marginBottom: 6,
    marginTop: "var(--space-3)",
  },
  fieldInput: {
    width: "100%",
    border: "none",
    borderBottom: "1px solid var(--comp-rule)",
    borderRadius: 0,
    padding: "4px 0",
    height: "var(--control-h)",
    fontSize: 12,
    fontFamily: "var(--font-mono)",
    background: "transparent",
    color: "var(--fg-1)",
    outline: "none",
  },
};

const modalStyles: Record<string, React.CSSProperties> = {
  overlay: {
    position: "fixed" as const,
    inset: 0,
    background: "var(--overlay-deep)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  },
  card: {
    background: "var(--comp-paper)",
    border: "1px solid var(--comp-rule)",
    borderRadius: "var(--radius-lg)",
    padding: "var(--space-4)",
    width: 520,
    maxWidth: "90vw",
    maxHeight: "85vh",
    overflowY: "auto" as const,
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
  },
  heading: {
    fontSize: 13,
    fontWeight: 600,
    color: "var(--fg-1)",
    marginBottom: 16,
  },
  slugHint: {
    fontSize: 11,
    color: "var(--fg-3)",
    fontFamily: "var(--font-mono)",
    margin: "4px 0 0",
  },
  contentArea: {
    width: "100%",
    minHeight: 180,
    padding: "8px 0",
    border: "none",
    borderBottom: "1px solid var(--comp-rule)",
    borderRadius: 0,
    fontSize: 12,
    lineHeight: 1.6,
    fontFamily: "var(--font-mono)",
    background: "transparent",
    color: "var(--fg-1)",
    outline: "none",
    resize: "vertical" as const,
  },
  btnRow: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 8,
    marginTop: 16,
  },
};
